import { APP_COMMIT, APP_VERSION } from './version';

/**
 * The installed app and its updates (src/pwa/sw.js). Every release has a new service worker; as
 * soon as a page notices it (when it opens, when it comes back to the screen, and every quarter
 * of an hour) the worker caches the new release and takes over. The page is then told, and the
 * app reloads into the new release where the player was (src/ui/App.tsx). version.json is
 * checked as well, so a release is noticed even where service workers are not available.
 */

export interface ReleaseInfo {
  readonly version: string;
  readonly commit: string;
}

/** Whether the release the server describes is another one than the page's. */
export function isOtherRelease(remote: unknown, local: ReleaseInfo): boolean {
  if (typeof remote !== 'object' || remote === null) return false;
  const r = remote as Partial<ReleaseInfo>;
  if (typeof r.version !== 'string' || typeof r.commit !== 'string') return false;
  return r.version !== local.version || r.commit !== local.commit;
}

const CHECK_EVERY_MS = 15 * 60 * 1000;
/** How long a new release may take to install and take over before the page reloads anyway. */
const TAKEOVER_WAIT_MS = 20_000;

/**
 * Registers the service worker (production builds) and calls `onUpdate` once a new release is
 * ready: when its worker has taken over from the old one, or, without a worker, as soon as
 * version.json names another release. Returns a function that stops the checks.
 */
export function watchForUpdates(onUpdate: () => void): () => void {
  if (!import.meta.env.PROD || typeof window === 'undefined') return () => undefined;
  const sw = 'serviceWorker' in navigator ? navigator.serviceWorker : null;
  let told = false;
  let fallback: ReturnType<typeof setTimeout> | undefined;
  const tell = () => {
    if (told) return;
    told = true;
    clearTimeout(fallback);
    onUpdate();
  };

  // The first worker takes over a page that had none: that is an install, not an update.
  const hadController = !!sw?.controller;
  const onControllerChange = () => {
    if (hadController) tell();
  };
  sw?.addEventListener('controllerchange', onControllerChange);
  const registered: Promise<ServiceWorkerRegistration | null> = sw
    ? sw.register('./sw.js', { updateViaCache: 'none' }).catch(() => null)
    : Promise.resolve(null);

  const check = async () => {
    const registration = await registered;
    try {
      await registration?.update();
    } catch {
      // Offline, or the server is busy: the next check will tell.
    }
    try {
      const res = await fetch('./version.json', { cache: 'no-store' });
      if (!res.ok) return;
      const remote: unknown = await res.json();
      if (!isOtherRelease(remote, { version: APP_VERSION, commit: APP_COMMIT })) return;
      // The worker installs the new release and takes over; if it cannot, reload anyway.
      if (!registration) tell();
      else if (fallback === undefined) fallback = setTimeout(tell, TAKEOVER_WAIT_MS);
    } catch {
      // Offline: nothing to compare with.
    }
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') void check();
  };
  document.addEventListener('visibilitychange', onVisible);
  const timer = setInterval(() => void check(), CHECK_EVERY_MS);
  const first = setTimeout(() => void check(), 3000);
  return () => {
    sw?.removeEventListener('controllerchange', onControllerChange);
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(timer);
    clearTimeout(first);
    clearTimeout(fallback);
  };
}

/** Where the player was when the app reloaded into a new release, for the new page to reopen. */
export interface UpdateResume {
  readonly screen: string;
  /** The release the page was running before. */
  readonly from: string;
}

const RESUME_KEY = 'pawned.update.v1';

export function rememberForUpdate(screen: string): void {
  try {
    sessionStorage.setItem(RESUME_KEY, JSON.stringify({ screen, from: APP_VERSION }));
  } catch {
    // No session storage: the new release opens on the menu.
  }
}

/** The place to reopen after an update (read once: a later reload opens normally). */
export function takeUpdateResume(): UpdateResume | null {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY);
    sessionStorage.removeItem(RESUME_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<UpdateResume>;
    return typeof v.screen === 'string' && typeof v.from === 'string'
      ? { screen: v.screen, from: v.from }
      : null;
  } catch {
    return null;
  }
}

/*
 * Holding the reload: while a turn is being put together (drafted moves not sent yet) the update
 * waits, and happens as soon as the turn is ended or taken back.
 */
const holds = new Set<string>();
const released = new Set<() => void>();

export function holdUpdate(reason: string, hold: boolean): void {
  if (hold) holds.add(reason);
  else if (holds.delete(reason) && holds.size === 0) for (const l of [...released]) l();
}

export const updateHeld = (): boolean => holds.size > 0;

export function onUpdateReleased(listener: () => void): () => void {
  released.add(listener);
  return () => released.delete(listener);
}
